// Constants
import { ClientSurface } from "@ms/uno-constants/lib/local/AppConstants";
import { RequestHeaders } from "@ms/uno-constants/lib/local/ServiceConstants";
// Services
import { AuthenticatedAjaxClient } from "@ms/uno-services/lib/local/ajaxClients/AuthenticatedAjaxClient";
import { CorrelationSettingAjaxClient } from "@ms/uno-services/lib/local/ajaxClients/CorrelationSettingAjaxClient";
import { GatedGetAjaxClient } from "@ms/uno-services/lib/local/ajaxClients/GatedGetAjaxClient";
import { PlannerAjaxClient } from "@ms/uno-services/lib/local/ajaxClients/PlannerAjaxClient";
import { TasksPlatformAjaxClient } from "@ms/uno-services/lib/local/ajaxClients/TasksPlatformAjaxClient";
import { TasksPlatformHeaderAjaxClient } from "@ms/uno-services/lib/local/ajaxClients/TasksPlatformTelemetryAjaxClient";
import { ThrottleAjaxClient } from "@ms/uno-services/lib/local/ajaxClients/ThrottleAjaxClient";
import { TokenRefreshAndRetryAjaxClient } from "@ms/uno-services/lib/local/ajaxClients/TokenRefreshAndRetryAjaxClient";
/**
 * Class for managing setup of services for the app
 */
export class BaseServiceOrchestrator {
    /**
     * Initialize an instance of the BaseServiceOrchestrator
     * @param serviceFactory The service factory
     * @param requestPrioritizer The request prioritizer for priority based queuing
     * @param configProvider The configuration provider
     * @param loggers The logger context
     * @param getAccessTokenDelegate The delegate for getting an access token
     * @param refreshAccessTokenDelegate The delegate for refreshing an access token
     */
    constructor(serviceFactory, requestPrioritizer, configProvider, loggers, ajaxClient, 
    /**
     * Bottom ajax client for the Planner service chain. Subclasses pass a client with
     * `CorrelationSettingAjaxClient` *inside* `RetryAjaxClient` so every transient retry —
     * not just 401 token-refresh retries — gets a fresh `X-CorrelationId`. Built via
     * `buildRetryAjaxClientWithCorrelation` in production; pass the shared `ajaxClient`
     * when per-retry correlation is not needed (e.g. dev/fake environments).
     */
    plannerAjaxClient, getAccessTokenDelegate, refreshAccessTokenDelegate) {
        this.serviceFactory = serviceFactory;
        this.requestPrioritizer = requestPrioritizer;
        this.configProvider = configProvider;
        this.loggers = loggers;
        this.ajaxClient = ajaxClient;
        this.plannerAjaxClient = plannerAjaxClient;
        this.getAccessTokenDelegate = getAccessTokenDelegate;
        this.refreshAccessTokenDelegate = refreshAccessTokenDelegate;
    }
    /**
     * Wraps an ajax client with `ThrottleAjaxClient` to enforce per-service concurrency limits and a
     * 429 circuit breaker that honors the `Retry-After` header. Returns the input client unchanged when
     * the `DisableEvoServiceThrottling` change gate is on, which serves as the rollback switch for
     * services other than Planner, Todo and Graph (which have their own gates).
     *
     * Each call constructs a fresh `ThrottleAjaxClient` instance, so every service gets its own
     * independent concurrency cap and circuit-breaker state. Callers typically place this between
     * `GatedGetAjaxClient` (outer, deduplicates GETs before slots are taken) and the auth/token-refresh
     * chain (inner) so deduplicated requests don't waste throttle slots.
     */
    throttleClient(client) {
        if (this.configProvider().changeGates.DisableEvoServiceThrottling) {
            return client;
        }
        return new ThrottleAjaxClient(client, this.configProvider().settings.throttleSettings, this.loggers);
    }
    /**
     * Get the Adaptive Card service provider
     */
    getAdaptiveCardServiceProvider() {
        const { retailservices } = this.configProvider().settings.serviceConfigurations;
        // AdaptiveCard is a newer EVO service: gated by DisableEvoServiceThrottling.
        const apiClient = new CorrelationSettingAjaxClient(new GatedGetAjaxClient(this.throttleClient(new TokenRefreshAndRetryAjaxClient(new AuthenticatedAjaxClient(this.ajaxClient, retailservices.authResourceURI, this.loggers, this.getAccessTokenDelegate, retailservices.authSilent), retailservices.authResourceURI, this.refreshAccessTokenDelegate, this.loggers, retailservices.authSilent))), RequestHeaders.ClientRequestId, this.loggers);
        return this.serviceFactory.getAdaptiveCardServiceProvider(apiClient, this.configProvider);
    }
    /**
     * Get the Planner service provider
     */
    getPlannerServiceProvider() {
        const { planner } = this.configProvider().settings.serviceConfigurations;
        const plannerClient = new PlannerAjaxClient(new TasksPlatformHeaderAjaxClient(new TasksPlatformAjaxClient(new AuthenticatedAjaxClient(this.plannerAjaxClient, planner.authResourceURI, this.loggers, this.getAccessTokenDelegate, planner.authSilent), planner.authResourceURI, this.refreshAccessTokenDelegate, this.loggers, planner.authSilent), this.configProvider, ClientSurface.Evo));
        // Planner stays on the existing `DisableServiceThrottling` gate (pre-Bug 3065174 behavior),
        // so an issue in any of the newly throttled EVO services can be mitigated via
        // `DisableEvoServiceThrottling` without disabling Planner throttling.
        const throttledClient = this.configProvider().changeGates.DisableServiceThrottling
            ? plannerClient
            : new ThrottleAjaxClient(plannerClient, this.configProvider().settings.throttleSettings, this.loggers);
        const apiClient = new GatedGetAjaxClient(throttledClient);
        return this.serviceFactory.getPlannerServiceProvider(apiClient, this.requestPrioritizer, this.configProvider);
    }
}
